import { useState, useEffect } from "react";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { Plus, Pencil, Trash } from "@phosphor-icons/react";

export default function MatchingRulesPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", level: 1, fields: "", match_type: "exact", min_score: 90, is_active: true, notes: "" });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await API.get("/matching-rules");
      setRules(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...form,
        level: parseInt(form.level) || 1,
        min_score: parseInt(form.min_score) || 90,
        fields: form.fields.split(",").map((f) => f.trim()).filter(Boolean),
      };
      if (editing) {
        await API.put(`/matching-rules/${editing}`, payload);
      } else {
        await API.post("/matching-rules", payload);
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: "", level: 1, fields: "", match_type: "exact", min_score: 90, is_active: true, notes: "" });
      load();
    } catch (e) { alert("Save failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this rule?")) return;
    try { await API.delete(`/matching-rules/${id}`); load(); } catch { alert("Delete failed"); }
  };

  const openEdit = (r) => {
    setEditing(r.id);
    setForm({
      name: r.name,
      level: r.level,
      fields: (r.fields || []).join(", "),
      match_type: r.match_type,
      min_score: r.min_score,
      is_active: r.is_active,
      notes: r.notes || "",
    });
    setShowForm(true);
  };

  const LEVEL_LABELS = { 1: "Exact", 2: "Alternate", 3: "Cross-Sheet", 4: "Fuzzy" };

  return (
    <div data-testid="matching-rules-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 font-heading">Matching Rules</h1>
          <p className="text-sm text-zinc-500 mt-1">Define how records are matched against sheets</p>
        </div>
        <Dialog open={showForm} onOpenChange={(o) => { setShowForm(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-rule-btn" className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm gap-2"><Plus size={16} /> Add Rule</Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit" : "Add"} Matching Rule</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-4">
              <FF label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="rule-name" />
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Level</Label>
                  <select data-testid="rule-level" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm">
                    <option value={1}>1 - Exact</option>
                    <option value={2}>2 - Alternate</option>
                    <option value={3}>3 - Cross-Sheet</option>
                    <option value={4}>4 - Fuzzy</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Match Type</Label>
                  <select data-testid="rule-match-type" value={form.match_type} onChange={(e) => setForm({ ...form, match_type: e.target.value })} className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm">
                    <option value="exact">Exact</option>
                    <option value="alternate">Alternate</option>
                    <option value="fuzzy">Fuzzy</option>
                  </select>
                </div>
              </div>
              <FF label="Fields (comma-separated)" value={form.fields} onChange={(v) => setForm({ ...form, fields: v })} testId="rule-fields" />
              <FF label="Min Score" value={form.min_score} onChange={(v) => setForm({ ...form, min_score: v })} type="number" testId="rule-min-score" />
              <div className="flex items-center gap-2">
                <Switch data-testid="rule-active" checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <Label className="text-sm text-zinc-700">Active</Label>
              </div>
              <FF label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testId="rule-notes" />
              <Button data-testid="save-rule-btn" onClick={handleSubmit} className="w-full bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm">{editing ? "Update" : "Create"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-zinc-200 rounded-sm">
        <table className="w-full">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50">
              <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Name</th>
              <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Level</th>
              <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Fields</th>
              <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Type</th>
              <th className="text-right px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Min Score</th>
              <th className="text-center px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Active</th>
              <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-zinc-400">Loading...</td></tr>
            ) : rules.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-zinc-400">No matching rules configured</td></tr>
            ) : (
              rules.map((r) => (
                <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`rule-row-${r.id}`}>
                  <td className="px-4 py-2 text-sm text-zinc-900">{r.name}</td>
                  <td className="px-4 py-2 text-xs font-mono">{LEVEL_LABELS[r.level] || r.level}</td>
                  <td className="px-4 py-2 text-xs font-mono text-zinc-600">{(r.fields || []).join(", ")}</td>
                  <td className="px-4 py-2 text-xs font-mono uppercase">{r.match_type}</td>
                  <td className="px-4 py-2 text-sm font-mono text-right">{r.min_score}</td>
                  <td className="px-4 py-2 text-center">
                    <span className={`inline-block w-2 h-2 rounded-full ${r.is_active ? "bg-emerald-500" : "bg-zinc-300"}`} />
                  </td>
                  <td className="px-4 py-2 flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(r)} className="text-zinc-400 hover:text-zinc-900"><Pencil size={14} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(r.id)} className="text-zinc-400 hover:text-red-600"><Trash size={14} /></Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FF({ label, value, onChange, type = "text", testId }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">{label}</Label>
      <Input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="rounded-sm border-zinc-300" />
    </div>
  );
}
